import { toast } from "@/lib/toast";

const MIC_TOAST_ID = "voice-input-error";

/** Build an actionable toast for mic/voice-input failures. The raw error
 * string from `useVoiceInput` is usually a DOMException name prefix (e.g.
 * `NotAllowedError: ...`) or a Web Speech error code; map those to a concrete
 * next step instead of surfacing the raw technical message. */
export async function reportMicError(raw: string) {
  let permissionState: PermissionState | null = null;
  try {
    const p = await navigator.permissions.query({ name: "microphone" as PermissionName });
    permissionState = p.state;
  } catch {
    // Permissions API unsupported — fall through to generic handling.
  }

  const token = raw.toLowerCase();
  const looksBlocked =
    permissionState === "denied" ||
    token.includes("notallowed") ||
    token.includes("not-allowed") ||
    token.includes("permission") ||
    token.includes("service-not-allowed");
  const noDevice = token.includes("notfound") || token.includes("devicesnotfound") || token.includes("no-speech");
  const inUse = token.includes("notreadable") || token.includes("trackstart");

  if (looksBlocked) {
    toast.error("Microphone blocked", {
      id: MIC_TOAST_ID,
      duration: 12000,
      description:
        "Click the lock/site-info icon in the address bar → Site settings → Microphone → Allow, then reload. On macOS, also enable Chrome under System Settings → Privacy & Security → Microphone.",
    });
    return;
  }
  if (noDevice) {
    toast.error("No microphone detected", {
      id: MIC_TOAST_ID,
      description: "Plug in a mic or select one under System Settings → Sound → Input.",
    });
    return;
  }
  if (inUse) {
    toast.error("Microphone in use", {
      id: MIC_TOAST_ID,
      description: "Another app or tab is holding the mic. Close it and try again.",
    });
    return;
  }
  toast.error("Microphone error", {
    id: MIC_TOAST_ID,
    description: raw || "Unable to start voice input.",
  });
}
